import { useState } from 'react'
import {
  Box,
  BottomNavigation,
  BottomNavigationAction,
  IconButton,
  Menu,
  MenuItem,
  Paper,
} from '@mui/material'
import MapIcon from '@mui/icons-material/Map'
import ViewListIcon from '@mui/icons-material/ViewList'
import TimelineIcon from '@mui/icons-material/Timeline'
import FilterListIcon from '@mui/icons-material/FilterList'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined'
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined'

export const BOTTOM_NAV_H = 56

/**
 * Phone chrome: one surface at a time, reusing desktop panels as overlays.
 * Map stays mounted underneath so Google Maps stays warm.
 */
export default function MobileAppShell({
  pane,
  onPaneChange,
  logout,
  isAdmin = false,
  onAdmin,
  filtersActive = false,
  mapContent,
  casesContent,
  timelineContent,
  filtersContent,
}) {
  const [menuAnchor, setMenuAnchor] = useState(null)
  const showOverlay = pane === 'cases' || pane === 'timeline' || pane === 'filters'
  const mapVisible = pane === 'map'
  const timelineVisible = pane === 'timeline'

  return (
    <Box
      sx={{
        position: 'relative',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        overflowX: 'clip',
        width: '100%',
        maxWidth: '100%',
        bgcolor: '#f8f9fa',
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          minHeight: 44,
          bgcolor: '#fff',
          borderBottom: '1px solid #e5e7eb',
          zIndex: 20,
        }}
      >
        <img src="/ls-logo.svg" alt="LandSentry" style={{ height: 22 }} />
        <IconButton
          size="small"
          aria-label="More"
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          sx={{ color: '#4b5563' }}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {isAdmin && (
            <MenuItem
              onClick={() => {
                setMenuAnchor(null)
                onAdmin?.()
              }}
            >
              <AdminPanelSettingsOutlinedIcon sx={{ mr: 1, fontSize: 20 }} />
              Admin
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              setMenuAnchor(null)
              logout?.()
            }}
            sx={{ color: '#b45353' }}
          >
            <LogoutOutlinedIcon sx={{ mr: 1, fontSize: 20 }} />
            Log out
          </MenuItem>
        </Menu>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, position: 'relative', overflow: 'hidden' }}>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            visibility: mapVisible ? 'visible' : 'hidden',
            pointerEvents: mapVisible ? 'auto' : 'none',
            zIndex: 1,
          }}
          aria-hidden={!mapVisible}
        >
          {mapContent}
        </Box>

        {/* Timeline stays mounted (like Map) so Virtuoso scroll position is preserved across tabs. */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: timelineVisible ? 10 : 2,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#fff',
            overflow: 'hidden',
            visibility: timelineVisible ? 'visible' : 'hidden',
            pointerEvents: timelineVisible ? 'auto' : 'none',
          }}
          aria-hidden={!timelineVisible}
        >
          {timelineContent}
        </Box>

        {showOverlay && pane !== 'timeline' && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 10,
              display: 'flex',
              flexDirection: 'column',
              bgcolor: '#fff',
              overflow: 'hidden',
            }}
          >
            {pane === 'cases' && (
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                {casesContent}
              </Box>
            )}
            {pane === 'filters' && (
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                {filtersContent}
              </Box>
            )}
          </Box>
        )}
      </Box>

      <Paper
        elevation={8}
        square
        sx={{
          flexShrink: 0,
          zIndex: 30,
          borderTop: '1px solid #e5e7eb',
          pb: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        <BottomNavigation
          value={pane}
          onChange={(_e, next) => onPaneChange(next)}
          showLabels
          sx={{
            height: BOTTOM_NAV_H,
            '& .MuiBottomNavigationAction-root': {
              minWidth: 0,
              py: 0.5,
              color: '#6b7280',
              '&.Mui-selected': { color: '#7454A5' },
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.65rem',
              '&.Mui-selected': { fontSize: '0.65rem' },
            },
          }}
        >
          <BottomNavigationAction value="map" label="Map" icon={<MapIcon />} />
          <BottomNavigationAction value="cases" label="Cases" icon={<ViewListIcon />} />
          <BottomNavigationAction value="timeline" label="Timeline" icon={<TimelineIcon />} />
          <BottomNavigationAction
            value="filters"
            label="Filters"
            icon={
              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <FilterListIcon />
                {filtersActive && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -2,
                      right: -4,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: '#7454A5',
                    }}
                  />
                )}
              </Box>
            }
          />
        </BottomNavigation>
      </Paper>
    </Box>
  )
}
